// Household-wide Living Atlas settings, stored as one JSON value under the
// `atlas` key of household_setting (the same table as every other household
// setting). Per-device state — the last mode, the map view per mode, layer
// toggles — stays in the browser.

import { ATLAS_MODE_IDS, isAtlasModeId, type LocationPresetId } from "./modes";
import type { AtlasModeId } from "./types";

export interface AtlasPlace {
  id: string;
  name: string;
  lat: number;
  lon: number;
  zoom: number;
}

export interface LayerDefault {
  on: boolean;
  /** 0–1 */
  opacity: number;
}

export interface AtlasSettings {
  /** Master switch for the whole screen. */
  enabled: boolean;
  /** Every mode exactly once, in switcher order. */
  order: AtlasModeId[];
  hidden: AtlasModeId[];
  favorites: AtlasModeId[];
  defaultMode: AtlasModeId;
  defaultPreset: LocationPresetId;
  /** Modes the ambient rotation cycles through. */
  ambientModes: AtlasModeId[];
  ambientIntervalSec: number;
  /** Rotation pauses on a touch and resumes after this long without one. */
  ambientResumeSec: number;
  /** Fewer animation frames, lower pixel ratio, smaller tile cache (Pi 4/5 with 2 GB). */
  lowPower: boolean;
  /** No pulses, fades or auto-play, whatever the device says. */
  reducedMotion: boolean;
  /** Extra location presets beyond Home / Colorado / United States / World. */
  places: AtlasPlace[];
  /** Per-mode layer defaults, keyed by mode then layer id. */
  layerDefaults: Partial<Record<AtlasModeId, Record<string, LayerDefault>>>;
  /** Show the admin performance panel on this household's screens (admins only). */
  debugPanel: boolean;
}

export const DEFAULT_ATLAS_SETTINGS: AtlasSettings = {
  enabled: true,
  order: [...ATLAS_MODE_IDS],
  hidden: [],
  favorites: ["earthpulse", "aurora-live", "live-earth", "firewatch", "earth-at-night"],
  defaultMode: "earthpulse",
  defaultPreset: "world",
  ambientModes: ["earth-at-night", "earthpulse", "aurora-live", "live-earth"],
  ambientIntervalSec: 90,
  ambientResumeSec: 120,
  lowPower: false,
  reducedMotion: false,
  places: [],
  layerDefaults: {},
  debugPanel: false,
};

const PRESET_IDS: LocationPresetId[] = ["home", "colorado", "us", "world"];

function modeList(v: unknown, field: string, max = ATLAS_MODE_IDS.length): AtlasModeId[] {
  if (!Array.isArray(v) || v.length > max) throw new Error(`${field} must be a list of modes`);
  const out: AtlasModeId[] = [];
  for (const id of v) {
    if (!isAtlasModeId(id)) throw new Error(`Unknown mode in ${field}`);
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

const intIn = (v: unknown, min: number, max: number, field: string) => {
  const n = Number(v);
  if (!Number.isInteger(n) || n < min || n > max) throw new Error(`${field} must be ${min}–${max}`);
  return n;
};

/**
 * Complete an order so it lists every mode exactly once: known modes keep
 * their place, modes added in a later version join at the end.
 */
export function completeOrder(order: readonly string[]): AtlasModeId[] {
  const out: AtlasModeId[] = [];
  for (const id of order) if (isAtlasModeId(id) && !out.includes(id)) out.push(id);
  for (const id of ATLAS_MODE_IDS) if (!out.includes(id)) out.push(id);
  return out;
}

/** Merge a stored value over the defaults, dropping anything no longer valid. */
export function normalizeAtlasSettings(stored: unknown): AtlasSettings {
  const s = stored && typeof stored === "object" && !Array.isArray(stored) ? (stored as Record<string, unknown>) : {};
  const merged = { ...DEFAULT_ATLAS_SETTINGS, ...s } as AtlasSettings;
  const ids = (v: unknown, fallback: AtlasModeId[]) => (Array.isArray(v) ? v.filter(isAtlasModeId) : fallback);
  return {
    ...merged,
    enabled: merged.enabled !== false,
    order: completeOrder(Array.isArray(s.order) ? (s.order as string[]) : DEFAULT_ATLAS_SETTINGS.order),
    hidden: ids(s.hidden, DEFAULT_ATLAS_SETTINGS.hidden),
    favorites: ids(s.favorites, DEFAULT_ATLAS_SETTINGS.favorites),
    ambientModes: ids(s.ambientModes, DEFAULT_ATLAS_SETTINGS.ambientModes),
    defaultMode: isAtlasModeId(s.defaultMode) ? s.defaultMode : DEFAULT_ATLAS_SETTINGS.defaultMode,
    defaultPreset: PRESET_IDS.includes(s.defaultPreset as LocationPresetId) ? (s.defaultPreset as LocationPresetId) : DEFAULT_ATLAS_SETTINGS.defaultPreset,
    places: Array.isArray(s.places) ? (s.places as AtlasPlace[]) : [],
    layerDefaults: s.layerDefaults && typeof s.layerDefaults === "object" ? (s.layerDefaults as AtlasSettings["layerDefaults"]) : {},
  };
}

/** Validate a partial update from the admin page. Throws with a readable message. */
export function validateAtlasPatch(input: unknown): Partial<AtlasSettings> {
  if (!input || typeof input !== "object" || Array.isArray(input)) throw new Error("Atlas settings must be an object");
  const raw = input as Record<string, unknown>;
  const out: Partial<AtlasSettings> = {};
  if ("enabled" in raw) out.enabled = raw.enabled === true;
  if ("order" in raw) out.order = completeOrder(modeList(raw.order, "order"));
  if ("hidden" in raw) out.hidden = modeList(raw.hidden, "hidden");
  if ("favorites" in raw) out.favorites = modeList(raw.favorites, "favorites");
  if ("ambientModes" in raw) out.ambientModes = modeList(raw.ambientModes, "ambientModes");
  if ("defaultMode" in raw) {
    if (!isAtlasModeId(raw.defaultMode)) throw new Error("Unknown default mode");
    out.defaultMode = raw.defaultMode;
  }
  if ("defaultPreset" in raw) {
    if (!PRESET_IDS.includes(raw.defaultPreset as LocationPresetId)) throw new Error("Unknown default location");
    out.defaultPreset = raw.defaultPreset as LocationPresetId;
  }
  if ("ambientIntervalSec" in raw) out.ambientIntervalSec = intIn(raw.ambientIntervalSec, 20, 1800, "Rotation interval (seconds)");
  if ("ambientResumeSec" in raw) out.ambientResumeSec = intIn(raw.ambientResumeSec, 15, 3600, "Resume after (seconds)");
  if ("lowPower" in raw) out.lowPower = raw.lowPower === true;
  if ("reducedMotion" in raw) out.reducedMotion = raw.reducedMotion === true;
  if ("debugPanel" in raw) out.debugPanel = raw.debugPanel === true;
  if ("places" in raw) {
    if (!Array.isArray(raw.places) || raw.places.length > 12) throw new Error("Up to 12 places");
    out.places = raw.places.map((p, i) => {
      const o = (p ?? {}) as Record<string, unknown>;
      const lat = Number(o.lat);
      const lon = Number(o.lon);
      const zoom = Number(o.zoom ?? 7);
      const name = typeof o.name === "string" ? o.name.trim().slice(0, 40) : "";
      if (!name || !Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 85 || Math.abs(lon) > 180) throw new Error("Each place needs a name and a position");
      if (!Number.isFinite(zoom) || zoom < 1 || zoom > 14) throw new Error("Zoom must be 1–14");
      // Places are stored to ~1 km, like the household location.
      return {
        id: typeof o.id === "string" && /^[\w-]{1,32}$/.test(o.id) ? o.id : `place${i}_${Date.now().toString(36)}`,
        name,
        lat: Math.round(lat * 100) / 100,
        lon: Math.round(lon * 100) / 100,
        zoom: Math.round(zoom * 10) / 10,
      };
    });
  }
  if ("layerDefaults" in raw) {
    const ld = raw.layerDefaults;
    if (!ld || typeof ld !== "object" || Array.isArray(ld)) throw new Error("Layer defaults must be an object");
    const next: AtlasSettings["layerDefaults"] = {};
    for (const [mode, layers] of Object.entries(ld)) {
      if (!isAtlasModeId(mode)) throw new Error("Unknown mode in layer defaults");
      if (!layers || typeof layers !== "object") continue;
      const m: Record<string, LayerDefault> = {};
      for (const [layer, v] of Object.entries(layers as Record<string, unknown>)) {
        if (!/^[\w-]{1,40}$/.test(layer)) throw new Error("Invalid layer id");
        const o = (v ?? {}) as Record<string, unknown>;
        const opacity = Number(o.opacity ?? 1);
        if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) throw new Error("Opacity must be 0–1");
        m[layer] = { on: o.on !== false, opacity: Math.round(opacity * 100) / 100 };
      }
      next[mode] = m;
    }
    out.layerDefaults = next;
  }
  return out;
}

/** The modes a household sees in the switcher, in their order. */
export function visibleModes(s: AtlasSettings): AtlasModeId[] {
  return s.order.filter((id) => !s.hidden.includes(id));
}

/** Next mode in an ambient rotation (wraps; starts at the first if the current isn't listed). */
export function nextAmbientMode(list: AtlasModeId[], current: AtlasModeId): AtlasModeId | null {
  if (list.length === 0) return null;
  const i = list.indexOf(current);
  return list[(i + 1) % list.length] ?? null;
}
